import React from 'react';
import {LaneHealthAndContextDisplay} from './PrimaryDisplay'
import * as messages from '@radnuc/messages';
import * as d3 from 'd3';
import { UserInvestigationInfo } from './UserInvestigationDisplay'
import { TimeDisplay } from './StatusComponents'
import { uuidToString } from './UUID';

import './SecondaryDisplay.css'

import icon_truck_logo from './img/truck-front-icon-white.png'
import icon_car_logo from './img/car-front-icon-white.png'

type GammaCount = { time: number, sum: number, dose: number, num_det:number, livetime: number }
type NeutronCount = { time: number, count: number, num_det: number, livetime: number }
type Investigation = messages.Rad.Investigation | messages.Encounter.Encounter;

interface SecondaryDisplayProps {
    radiationData: messages.Rad.RadiationReading[] | null,
    liveRadiationData: messages.Rad.RadiationReading[] | null,
    neutronData: messages.Rad.NeutronReading[] | null,
    liveNeutronData: messages.Rad.NeutronReading[] | null,
    investigations: Investigation[],
    replayInvestigation?: Investigation,
    replayTime?: Date,
    investigationsNotAcknowledged?: Investigation[],
    liveStream: boolean,
    isCommercial?: boolean,
    userInvestigationInfo: UserInvestigationInfo,
    invReplayTransition?: boolean
}

export class SecondaryDisplay extends React.Component<SecondaryDisplayProps> {
    gammaCounts: GammaCount[];
    neutronCounts: NeutronCount[];
    maxGammaCountAllData: number = 0;
    maxNeutronCountAllData: number = 0;

    lastTimeForceRedraw: number = 0;
    dominantIsotope: string | null = null;

    lanesTotal:number = 1;
    lanePosition:number = 0;

    constructor(props: SecondaryDisplayProps) {
        super(props)

        //this.spectra = [];
        this.gammaCounts = [];
        this.neutronCounts = [];
    }

    addVector(a: number[], b: number[] | null) {
        if (b !== null)
            return a.map((e, i) => e + b[i]);
        else
            return a;
    }


    calcGammaCount() {
        //Calculates count sum and average spectrum of all detectors
        this.gammaCounts = [];
        this.maxGammaCountAllData = 0;
        if (this.props.radiationData !== null && this.props.radiationData.length > 0) {
            this.props.radiationData.forEach((data) => {
                let this_lane_sum_spectrum = 0.0;
                let this_lane_livetime = 0.0;
                let this_lane_dose = 0.0;
                let this_lane_num_det = 0;
                let count_all_lanes:number[] = Array(this.lanesTotal).fill(0);
                let livetime_all_lanes:number[] = Array(this.lanesTotal).fill(0);
                data.detectors.forEach((det) => {
                    if(det.spectrum !== null){
                        const sum = d3.sum(det.spectrum);
                        if(det.lane_position === this.lanePosition){
                            this_lane_sum_spectrum += sum;
                            this_lane_dose += det.dose_rate;
                            this_lane_livetime += det.livetime;
                            this_lane_num_det++;
                        }
                        count_all_lanes[det.lane_position] += sum;   //For plot scale across all lanes
                        livetime_all_lanes[det.lane_position] += det.livetime;
                    }
                });
                for(var i=0;i<this.lanesTotal;i++){
                    if(livetime_all_lanes[i] > 0){
                        count_all_lanes[i] = count_all_lanes[i]/livetime_all_lanes[i];
                    } else {
                        count_all_lanes[i] = 0;
                    }
                }

                const this_maxGammaAllLanes = d3.max(count_all_lanes) || 0;
                if(this.maxGammaCountAllData < this_maxGammaAllLanes) {
                    this.maxGammaCountAllData = this_maxGammaAllLanes;
                }


                if (this_lane_livetime === 0) {
                    this_lane_livetime = 1;
                    this_lane_sum_spectrum = 0;
                }

                var result = {
                    time: data.time,
                    sum: this_lane_sum_spectrum,
                    dose: this_lane_dose,
                    livetime: this_lane_livetime,
                    num_det: this_lane_num_det,
                }
                this.gammaCounts.push(result);
            });
        }
    }

    calcNeutronCount(){
        this.neutronCounts = [];
        this.maxNeutronCountAllData = 0;
        if (this.props.neutronData !== null && this.props.neutronData.length > 0) {
            this.props.neutronData.forEach((data) => {

                let this_lane_sum_count = 0.0;
                let this_lane_livetime = 0.0;
                let this_lane_num_det = 0;
                let count_all_lanes:number[] = Array(this.lanesTotal).fill(0);
                let livetime_all_lanes:number[] = Array(this.lanesTotal).fill(0);
                if(data.detectors !== undefined){
                    data.detectors.forEach((det) => {
                        if(det.lane_position === this.lanePosition){
                            this_lane_sum_count += det.count;
                            this_lane_livetime += det.livetime;
                            this_lane_num_det++;
                        }
                        count_all_lanes[det.lane_position] += det.count;   //For plot scale across all lanes
                        livetime_all_lanes[det.lane_position] += det.livetime;
                    });
                    const this_maxNeutronAllLanes = d3.max(count_all_lanes) || 0;
                    if(this.maxNeutronCountAllData < this_maxNeutronAllLanes) {
                        this.maxNeutronCountAllData = this_maxNeutronAllLanes;
                    }

                    if (this_lane_livetime === 0) {
                        this_lane_livetime = 1;
                        this_lane_sum_count = 0;
                    }

                    const result = {
                        time: data.time,
                        count: this_lane_sum_count,
                        livetime: this_lane_livetime,
                        num_det: this_lane_num_det,
                    };
                    this.neutronCounts.push(result);
                }
            });
        }
    };

    render() {
        this.calcGammaCount();
        this.calcNeutronCount();
        let currentInvestigation:Investigation | null = this.props.investigations[this.props.investigations.length-1];
        if (this.props.liveStream){
            if(currentInvestigation){
                if(currentInvestigation.finalized){
                    if(this.props.investigationsNotAcknowledged && this.props.investigationsNotAcknowledged.length > 0){
                        //Show the last Unacknowledged Investigation
                        currentInvestigation = this.props.investigationsNotAcknowledged[this.props.investigationsNotAcknowledged.length-1]
                    } else {
                        currentInvestigation = null;
                    }
                }
            }
        } else {
            if(this.props.replayInvestigation !== undefined){
                currentInvestigation = this.props.replayInvestigation;
            }
        }



        let invReplayTransition = false;
        if(this.gammaCounts.length ===0 && this.neutronCounts.length===0){
            invReplayTransition = true;
        } else if (this.props.invReplayTransition === true){
            invReplayTransition = true;
        } else {
            invReplayTransition = false;
        }

        return(
            <div style={{ display: 'flex', flex: '1', flexDirection: 'column', height: '100%',justifyContent:'space-between', padding:'10px'}}>
                <div id='secondary-time-container' style={{ display: 'flex', flexDirection: 'row', justifyContent:'space-between', paddingBottom:'20px'}}>
                    <TimeDisplay liveStream={this.props.liveStream} replayTime={this.props.replayTime} style={{width:'370px'}}/>
                    <ScantimeRemainingDisplay currentInvestigation={currentInvestigation} liveStream={this.props.liveStream}
                            userInvestigationInfo={this.props.userInvestigationInfo} invReplayTransition={invReplayTransition}/>
                </div>
                <SecondarySourceLocationDisplay currentInvestigation={currentInvestigation} liveStream={this.props.liveStream} isCommercial={this.props.isCommercial}
                            invReplayTransition={invReplayTransition}/>
                <div style={{display:'inline-block', flex:'none', height:'80px', width:'400px', alignSelf:'center',paddingTop:'30px'}}>
                    <LaneHealthAndContextDisplay lanePosition={0} gammaCounts={this.gammaCounts} neutronCounts={this.neutronCounts}
                                            investigations={this.props.investigations} replayInvestigation={this.props.replayInvestigation}
                                            investigationsNotAcknowledged={this.props.investigationsNotAcknowledged}
                                            liveStream={this.props.liveStream} isCommercial={this.props.isCommercial} isSecondary={true} showAttrImage={false}/>
                </div>
            </div>
        )
    }
}
export default SecondaryDisplay


//******************************************************************************

interface SecondarySourceLocationDisplayProps {
    currentInvestigation: Investigation | null,
    liveStream: boolean,
    invReplayTransition?: boolean,
    isCommercial?: boolean
}

export class SecondarySourceLocationDisplay extends React.Component<SecondarySourceLocationDisplayProps> {

    render() {

        let source_direction_body;
        if(this.props.isCommercial){
            //COV
            let source_direction_top_driver = "";
            let source_direction_bottom_driver = "";
            let source_direction_top_passenger = "";
            let source_direction_bottom_passenger = "";

            if(this.props.currentInvestigation && !this.props.invReplayTransition){
                if(this.props.currentInvestigation !== null){
                    if(this.props.currentInvestigation.isotopes.length > 0){
                        if(this.props.currentInvestigation.isotopes[0].name === 'NEUTRONS'){
                            //NEUTRON ALERTS
                            if(this.props.currentInvestigation.source_lane_position !== null){
                                if(this.props.currentInvestigation.source_lane_position === 0){
                                    //DRIVER
                                    source_direction_bottom_driver = "alarm neutron";
                                    source_direction_top_driver = "alarm neutron";
                                } else if (this.props.currentInvestigation.source_lane_position === 1) {
                                    //PASSENGER
                                    source_direction_bottom_passenger = "alarm neutron";
                                    source_direction_top_passenger = "alarm neutron";
                                } else {
                                    console.log('Not a valid COV Neutron position');
                                    source_direction_bottom_driver = "alarm neutron";
                                    source_direction_top_driver = "alarm neutron";
                                    source_direction_bottom_passenger = "alarm neutron";
                                    source_direction_top_passenger = "alarm neutron";
                                }
                            } else {
                                source_direction_bottom_driver = "alarm neutron";
                                source_direction_top_driver = "alarm neutron";
                                source_direction_bottom_passenger = "alarm neutron";
                                source_direction_top_passenger = "alarm neutron";
                            }
                        } else {
                            //GAMMA ALERTS
                            if(this.props.currentInvestigation.source_lane_position !== null){
                                if(this.props.currentInvestigation.source_lane_position === 0){
                                    //BOTTOM DRIVER
                                    source_direction_bottom_driver = "alarm";
                                } else if (this.props.currentInvestigation.source_lane_position === 1) {
                                    //TOP DRIVER
                                    source_direction_top_driver = "alarm";
                                } else if (this.props.currentInvestigation.source_lane_position === 2) {
                                    //BOTTOM PASSENGER
                                    source_direction_bottom_passenger = "alarm";
                                } else if (this.props.currentInvestigation.source_lane_position === 3) {
                                    //TOP PASSENGER
                                    source_direction_top_passenger = "alarm";
                                } else {
                                    console.log('Not a valid COV Gamma position');
                                    source_direction_bottom_driver = "alarm";
                                    source_direction_top_driver = "alarm";
                                    source_direction_bottom_passenger = "alarm";
                                    source_direction_top_passenger = "alarm";
                                }
                            } else {
                                //ALL
                                source_direction_bottom_driver = "alarm";
                                source_direction_top_driver = "alarm";
                                source_direction_bottom_passenger = "alarm";
                                source_direction_top_passenger = "alarm";
                            }
                        }
                    }
                }
            }
                source_direction_body =
                    <div style={{ display: 'flex', flexDirection:'row', alignItems:'center', alignSelf:'center'}}>
                        <label className={"secondary-label"} style={{textAlign:'right'}}>PASSENGER</label>
                        <div style={{ display: 'flex', flexDirection:'column', alignItems:'center', alignSelf:'center'}}>
                        <label className={"secondary-label"}>TOP</label>
                        <div id={"secondary-source-direction-container"} className={"secondary-metric-container"} style={{ display: 'flex', flexDirection:'row'}}>
                            <div style={{ display: 'flex', flexDirection:'row', alignItems:'center', position: 'absolute'}}>
                                <div style={{display: 'flex', flexDirection:'column', alignItems:'center'}}>
                                    <img src={icon_truck_logo} style={{height:'400px',paddingLeft:'30px',paddingRight:'30px'}}/>
                                </div>
                            </div>
                            <div style={{ height: '100%', width:'500px', flex: '1 1 0%', display: 'flex', flexDirection:'column', paddingLeft:'25px'}}>
                                <div className={"secondary-source-direction-container "+source_direction_top_passenger}></div>
                                <div className={"secondary-source-direction-container "+source_direction_bottom_passenger}></div>
                            </div>
                            <div style={{ height: '100%', width:'500px', flex: '1 1 0%', display: 'flex', flexDirection:'column', paddingRight:'25px'}}>
                                <div className={"secondary-source-direction-container "+source_direction_top_driver}></div>
                                <div className={"secondary-source-direction-container "+source_direction_bottom_driver}></div>
                            </div>
                        </div>
                        <label className={"secondary-label"}>BOTTOM</label>
                        </div>
                        <label className={"secondary-label"} style={{textAlign:'left'}}>DRIVER</label>
                    </div>
        } else {
            //POV
            let source_direction_driver = "";
            let source_direction_passenger = "";


            if(this.props.currentInvestigation && !this.props.invReplayTransition){
                if(this.props.currentInvestigation !== null){
                    if(this.props.currentInvestigation.isotopes.length > 0){
                        if(this.props.currentInvestigation.isotopes[0].name === 'NEUTRONS'){
                            //NEUTRON ALERTS
                            if(this.props.currentInvestigation.source_lane_position !== null){
                                if(this.props.currentInvestigation.source_lane_position === 0){
                                    //DRIVER SIDE
                                    source_direction_driver = "alarm neutron";
                                } else if (this.props.currentInvestigation.source_lane_position === 1) {
                                    //PASSANGER SIDE
                                    source_direction_passenger = "alarm neutron";
                                } else {
                                    console.log('Not a valid POV position');
                                    source_direction_driver = "alarm neutron";
                                    source_direction_passenger = "alarm neutron";
                                }
                            } else {
                                //BOTH
                                source_direction_driver = "alarm neutron";
                                source_direction_passenger = "alarm neutron";
                            }
                        } else {
                            //GAMMA ALERTS
                            if(this.props.currentInvestigation.source_lane_position !== null){
                                if(this.props.currentInvestigation.source_lane_position === 0){
                                    //DRIVER SIDE
                                    source_direction_driver = "alarm";
                                } else if (this.props.currentInvestigation.source_lane_position === 1) {
                                    //PASSANGER SIDE
                                    source_direction_passenger = "alarm";
                                } else {
                                    console.log('Not a valid POV position');
                                    source_direction_driver = "alarm";
                                    source_direction_passenger = "alarm";
                                }
                            } else {
                                //BOTH
                                source_direction_driver = "alarm";
                                source_direction_passenger = "alarm";
                            }
                        }
                    }
                }
            }

            source_direction_body =
                <div style={{ display: 'flex', flexDirection:'row', alignItems:'center', alignSelf:'center'}}>
                    <label className={"secondary-label"} style={{textAlign:'right'}}>PASSENGER</label>
                    <div style={{ display: 'flex', flexDirection:'column', alignItems:'center', alignSelf:'center'}}>
                    <label className={"secondary-label"}>TOP</label>
                    <div id={"secondary-source-direction-container"} className={"secondary-metric-container"} style={{ display: 'flex', flexDirection:'row'}}>
                        <div style={{ display: 'flex', flexDirection:'row', alignItems:'center', position: 'absolute'}}>
                            <div style={{display: 'flex', flexDirection:'column', alignItems:'center'}}>
                                <img src={icon_car_logo} style={{height:'400px',paddingLeft:'30px',paddingRight:'30px'}}/>
                            </div>
                        </div>
                        <div style={{ height: '100%', width:'500px', flex: '1 1 0%', display: 'flex', flexDirection:'column', paddingLeft:'25px'}}>
                            <div className={"secondary-source-direction-container "+source_direction_passenger}></div>
                        </div>
                        <div style={{ height: '100%', width:'500px', flex: '1 1 0%', display: 'flex', flexDirection:'column', paddingRight:'25px'}}>
                            <div className={"secondary-source-direction-container "+source_direction_driver}></div>
                        </div>
                    </div>
                    <label className={"secondary-label"}>BOTTOM</label>
                    </div>
                    <label className={"secondary-label"} style={{textAlign:'left'}}>DRIVER</label>
                </div>
        }
        return (
            <>
            {source_direction_body}
            </>
        )
    }
}

//******************************************************************************

interface ScantimeRemainingDisplayProps {
    currentInvestigation: Investigation | null,
    liveStream: boolean,
    userInvestigationInfo?: UserInvestigationInfo,
    invReplayTransition?: boolean
}

export class ScantimeRemainingDisplay extends React.Component<ScantimeRemainingDisplayProps> {
    currentInvestigationId: String | undefined
    display_string: string="";
    scanDuration: number = 180000; //ms, must match UserInvestigation.handleSecondaryStartCancel()

    componentDidUpdate(prevProps: ScantimeRemainingDisplayProps){
        if(this.props.currentInvestigation !== undefined){
            if(this.props.currentInvestigation !== null){
                if(this.props.invReplayTransition === false){
                    if(!this.props.currentInvestigation.finalized){
                        this.currentInvestigationId = uuidToString(this.props.currentInvestigation.id);
                        let diff_time:number;
                        if(this.props.userInvestigationInfo?.lastResult){
                            diff_time = this.props.userInvestigationInfo.lastResult?.command.endTime - new Date().getTime();
                        } else {
                            diff_time = this.props.currentInvestigation.start_time+this.scanDuration - new Date().getTime();
                        }
                        if(diff_time <0){
                            this.display_string = '--';
                        } else {
                            this.display_string =  new Date(diff_time).toLocaleTimeString([], { minute: '2-digit', second: '2-digit' });
                        }
                    } else {
                        this.display_string = 'COMPLETED';
                    }
                } else {
                    this.display_string = 'WAIT';
                }
            } else {
                this.display_string = '--';
            }
        } else {
            this.display_string = 'N/A';
        }
    }

    render(){
        return(
            <div id={"secondary-scantime-remaining-container"} className="inv-metric-container" style={{}}>
                <label className="metric-title" style={{ fontSize:'1.2em'}}>Scan Time Remaining</label>
                <label className="metric" style={{ fontSize:'1.8em'}}>{this.display_string}</label>
            </div>
        )
    }
}
